import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AlphaModal from '@/features/alpha/components/AlphaModal';
import './Tooltip.css';

/**
 * The four corner anchors the floating chat bubble can snap to.
 * Persisted in localStorage so the choice survives reloads.
 */
type Corner = 'tl' | 'tr' | 'bl' | 'br';

const STORAGE_KEY = 'alpha-icon-corner';
const DEFAULT_CORNER: Corner = 'br';
const DRAG_THRESHOLD_PX = 4; // movement under this is a click, over is a drag

/**
 * Header height in pixels. The Layout's <Navbar> uses `h-16` (64 px) —
 * see `src/shared/components/Header/Navbar.tsx`. If you change the
 * navbar height, update this constant too: the bubble must never sit
 * inside the navbar band. At top corners we offset by
 * `HEADER_HEIGHT_PX + 16` so the bubble drops below the header with a
 * 16 px gap, and during drag we clamp the cursor-follow `top` to the
 * same minimum so users can't accidentally place it inside the navbar.
 */
const HEADER_HEIGHT_PX = 64;
const TOP_CLEARANCE_PX = HEADER_HEIGHT_PX + 16; // 80 px

const isCorner = (v: unknown): v is Corner =>
  v === 'tl' || v === 'tr' || v === 'bl' || v === 'br';

/** Read the saved corner from localStorage, falling back to bottom-right. */
const readSavedCorner = (): Corner => {
  if (typeof window === 'undefined') return DEFAULT_CORNER;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isCorner(v) ? v : DEFAULT_CORNER;
  } catch {
    return DEFAULT_CORNER;
  }
};

/** Pick the corner whose anchor point is closest to (x, y). */
const nearestCorner = (x: number, y: number): Corner => {
  const midX = window.innerWidth / 2;
  const midY = window.innerHeight / 2;
  return `${y < midY ? 't' : 'b'}${x < midX ? 'l' : 'r'}` as Corner;
};

/** Tailwind classes for each corner's snapped resting position. The
 * top-corner positions sit below the header (`top-20` = 80 px) so the
 * bubble never overlaps the sticky navbar. */
const CORNER_CLASSES: Record<Corner, string> = {
  tl: 'top-20 left-2',
  tr: 'top-20 right-2',
  bl: 'bottom-4 left-2',
  br: 'bottom-4 right-2',
};

/**
 * Tooltip placement classes. The tooltip pops out toward the
 * VIEWPORT-INTERIOR side so it never gets clipped by the screen edge,
 * and at TOP corners it drops BELOW the bubble (with a small gap) so
 * it can't extend up into the navbar band.
 *  • top-left     corner → tooltip drops down-and-right (`top-16 left-0`)
 *  • top-right    corner → tooltip drops down-and-left  (`top-16 right-0`)
 *  • bottom-left  corner → tooltip pops up-and-right    (`bottom-16 left-0`)
 *  • bottom-right corner → tooltip pops up-and-left     (`bottom-16 right-0`)
 * Each `*-16` value (64 px) clears the bubble (h-14 = 56 px) plus a
 * small visual gap.
 */
const TOOLTIP_CLASSES: Record<Corner, string> = {
  tl: 'top-16 left-0',
  tr: 'top-16 right-0',
  bl: 'bottom-16 left-0',
  br: 'bottom-16 right-0',
};

const AlphaIcon: React.FC = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [corner, setCorner] = useState<Corner>(readSavedCorner);

  // Drag state. While dragging we render the bubble at an absolute
  // (left, top) instead of the corner-anchored Tailwind classes, then
  // snap to the nearest corner on release. `dragOriginRef` tracks the
  // pointerdown spot so we can tell click vs drag (movement threshold).
  const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
  const dragOriginRef = useRef<{ x: number; y: number } | null>(null);
  const movedRef = useRef(false);

  // Ctrl+K: toggle the Alpha modal open/closed
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.ctrlKey && event.key === 'k') {
        event.preventDefault();
        setIsModalOpen((prev) => !prev);
        setShowTooltip(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Pointer drag handlers — using pointer events (vs HTML5 drag) so we
  // get smooth cursor-following on touch + mouse with a single API.
  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>): void => {
    // Don't start a drag for the keyboard "Enter" / "Space" triggered
    // synthetic pointer events, or for non-primary buttons.
    if (e.button !== 0) return;
    dragOriginRef.current = { x: e.clientX, y: e.clientY };
    movedRef.current = false;
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>): void => {
    const origin = dragOriginRef.current;
    if (!origin) return;
    const dx = e.clientX - origin.x;
    const dy = e.clientY - origin.y;
    if (!movedRef.current && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    movedRef.current = true;
    // Center the bubble on the cursor (the button is 56×56 = h-14 w-14).
    // Clamp `top` so the bubble can't be dragged into the sticky header
    // band — `TOP_CLEARANCE_PX` keeps it visually below the navbar even
    // mid-drag.
    const x = e.clientX - 28;
    const y = Math.max(TOP_CLEARANCE_PX, e.clientY - 28);
    setDragPos({ x, y });
    setShowTooltip(false);
  };

  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>): void => {
    const origin = dragOriginRef.current;
    dragOriginRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (movedRef.current && origin) {
      // Snap to the nearest corner, persist the choice.
      const next = nearestCorner(e.clientX, e.clientY);
      setCorner(next);
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* storage unavailable — silently fall back to in-memory only */
      }
      setDragPos(null);
      // IMPORTANT: don't reset `movedRef` here — the synthetic `click`
      // event fires AFTER `pointerup` (they're separate event paths;
      // preventDefault/stopPropagation on pointerup doesn't cancel the
      // click). The click handler reads this flag to skip opening the
      // modal when the interaction was a drag, and resets the flag
      // there.
    }
  };

  /**
   * Cancel handler — fires when the OS/browser aborts the gesture
   * mid-drag (pointer leaves the window, palm rejection on touch,
   * forced gesture cancel, etc.). Without this, `dragOriginRef` and
   * `dragPos` stay set forever, the bubble freezes mid-flight, and
   * `movedRef` blocks the next click from opening the modal. Mirror
   * `onPointerUp`'s cleanup but DON'T snap a new corner — the user
   * didn't choose to release here, so revert to the last saved corner.
   */
  const onPointerCancel = (e: React.PointerEvent<HTMLButtonElement>): void => {
    dragOriginRef.current = null;
    movedRef.current = false;
    setDragPos(null);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const onClick = (e: React.MouseEvent<HTMLButtonElement>): void => {
    // The browser fires `click` after every `pointerup`. If the
    // interaction was a drag, swallow this click so the modal doesn't
    // open. (Reset the flag here so the next true tap works.)
    if (movedRef.current) {
      movedRef.current = false;
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    setIsModalOpen(true);
    setShowTooltip(false);
  };

  // Position style: dragging → absolute (left/top) inline; resting →
  // corner-anchored Tailwind classes.
  const isDragging = dragPos !== null;
  const positionClass = isDragging ? '' : CORNER_CLASSES[corner];
  const positionStyle: React.CSSProperties = isDragging
    ? { left: dragPos.x, top: dragPos.y, right: 'auto', bottom: 'auto' }
    : {};

  return (
    <>
      {!isModalOpen && (
        <div
          className={`fixed z-50 ${positionClass}`}
          style={positionStyle}
          onMouseEnter={() => !isDragging && setShowTooltip(true)}
        >
          <div className="relative">
            {showTooltip && !isDragging && (
              <div
                // `data-corner` drives the per-corner arrow geometry
                // in Tooltip.css. The arrow has to point at the
                // bubble, which sits at a different relative position
                // depending on which corner the bubble has snapped
                // to:
                //   br → bubble is below-right of tooltip → arrow points DOWN-right
                //   bl → bubble is below-left of tooltip → arrow points DOWN-left
                //   tr → bubble is above-right of tooltip → arrow points UP-right
                //   tl → bubble is above-left of tooltip → arrow points UP-left
                // The previous CSS hardcoded a right-pointing arrow
                // (left: 100%) regardless of corner — pointed at empty
                // space in the default br case, breaking the tooltip's
                // visual anchor.
                data-corner={corner}
                className={`group tooltip-custom absolute w-max ${TOOLTIP_CLASSES[corner]}`}
              >
                <button
                  onClick={() => setShowTooltip(false)}
                  className="absolute -top-2 -left-2 rounded-full bg-header p-1 text-on-primary opacity-0 group-hover:opacity-100"
                >
                  <X size={16} />
                </button>
                <div className="sparkle-icon-background">
                  <Sparkles size={24} className="text-white" />
                </div>
                <div>
                  <p className="meet-alpha-text">{t('alpha.meetAlpha')}</p>
                  <p className="tooltip-description">
                    {t('alpha.meetAlphaDescription')}
                  </p>
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={onClick}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerCancel}
              className={`flex h-14 w-14 select-none items-center justify-center rounded-full bg-primary text-on-primary shadow-lg transition-colors focus:outline-none hover:bg-header touch-none ${
                isDragging ? 'cursor-grabbing scale-105' : 'cursor-grab animate-gentle-bounce'
              }`}
              aria-label={t('alpha.openChat')}
              data-testid="alpha-chat-bubble"
            >
              <MessageCircle size={32} aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
      {isModalOpen && <AlphaModal onClose={() => setIsModalOpen(false)} />}
    </>
  );
};

export default AlphaIcon;
